import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth0 } from "utils/react-auth0-wrapper";
import queryString from "query-string";
import { individualSite } from "../utils/determineSite";
import PaychexSsoLoading from "pages/signUp/individual/PaychexSsoLoading";
import { includes, toLower } from "lodash";
import useFontFaceObserver from "use-font-face-observer";

// public routes that send an already signed in user on to their onboarding/dashboard
const signupPaths = ["/", "/partner"];

interface PublicRouteProps {
  component?: React.ElementType;
  path?: string;
  [key: string]: unknown;
}

const PublicRoute = ({
  component: Component,
  path,
  ...props
}: PublicRouteProps) => {
  /* Mobile browsers kill asset requests during a redirect BUT Auth0 redirects that involve
      partner connections (like Paychex) can take some time to complete so the user will still see
       the icon UI and if assets don't load (like fonts) then text wont be visible */
  const isFontLoaded = useFontFaceObserver([{ family: "Graphik" }]);
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const location = useLocation();
  // Read from window.location, not useLocation: Auth0's onRedirectCallback
  // strips ?code=…&state=… via window.history.replaceState, which v6's router
  // doesn't observe — so location.search stays stale for the rest of the SPA's
  // life until the next navigate() call.
  const parsedSearch = queryString.parse(window.location.search);
  const { invitationCode, signup, referralCode, acr_values } = parsedSearch;
  const actualAcrValues =
    acr_values && acr_values === "ira_deductions" ? { acr_values } : {};

  // query-string types parsed values as string | (string | null)[] | null;
  // connection is always a single string in practice. Cast at this boundary.
  const connection = parsedSearch.connection as string | undefined;
  const isRootRoute = path === "/";
  const handleRootRoute = individualSite() && isRootRoute;
  // Auth0 is mid-callback (code+state in URL). Don't redirect — Auth0Provider's
  // onRedirectCallback will navigate to the appState targetUrl once it finishes.
  const isAuth0Callback = parsedSearch.code && parsedSearch.state;
  // If we have a pending post-login redirect in sessionStorage (set by
  // PrivateRoute/AuthApolloInitializer before loginWithRedirect), App's
  // useEffect will restore it once isAuthenticated settles. Don't fire
  // <Navigate to="/login"> in the meantime — that would push /login into
  // history and cascade through PrivateRoute's /login → /dashboard redirect.
  // Once the URL matches the stored target, the restore is complete and we
  // can proceed even if App's effect hasn't yet cleared the sessionStorage
  // marker (sessionStorage is not reactive, so we'd otherwise stay stuck
  // at null on routes like `/?invitationCode=…` that still match `/`).
  const stored =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem("auth0_postLoginRedirect")
      : null;
  const targetReached =
    stored && `${window.location.pathname}${window.location.search}` === stored;
  const hasPendingRedirect = stored && !targetReached;
  if (isAuth0Callback || hasPendingRedirect) {
    return null;
  }
  if (isAuthenticated) {
    // If window.location.pathname is no longer "/", an in-flight restoration
    // navigate (App's restoreEffect → navigate(stored)) has already moved us
    // off root, but react-router hasn't propagated that to useLocation yet —
    // so <Routes> matched "/" on stale state and we're rendering here. Firing
    // <Navigate to="/login/"> would clobber the restoration target (e.g. send
    // a refresh on /dashboard/transactions to /dashboard via /login's
    // buildLifecycleRoutes redirect). Return null and let the next render —
    // once router state catches up — match the real route.
    if (!includes(signupPaths, window.location.pathname)) {
      return null;
    }
    // Even though they went to a public route, they have an active session
    // Let's redirect them to our Home route that will get them to the right spot
    return <Navigate to={`/login/${window.location.search}`} replace />;
  } else if (handleRootRoute && isFontLoaded) {
    // If they are not authenticated and they are on the root route, we want to redirect them to the login page (may remove in future)
    const targetUrl = `${location.pathname}${
      location.search ? location.search : ""
    }`;
    // Persist across the Auth0 redirect so App.js can restore the URL on
    // return — onRedirectCallback strips all query params, so without this
    // any inviteId/invitationCode/etc. on the entry URL is lost.
    sessionStorage.setItem("auth0_postLoginRedirect", targetUrl);
    void loginWithRedirect({
      authorizationParams: {
        ...actualAcrValues,
        screen_hint:
          invitationCode || signup || referralCode ? "signup" : "login",
        ...(connection && { connection }),
      },
      appState: {
        targetUrl,
      },
    });
    if (toLower(connection) === "paychex") {
      return <PaychexSsoLoading />;
    }
    return null;
  } else {
    return Component ? <Component {...props} /> : <Outlet />;
  }
};

export default PublicRoute;
